import { useEffect, useState } from "react";
import { Bug } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { prototypeApi } from "@/lib/prototypeApi";

function Flag({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
      {ok ? "✓" : "✗"} {label}
    </span>
  );
}

export function OwnerDiagnostics({ open, onOpenChange }) {
  const [health, setHealth] = useState(null);
  const [config, setConfig] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { setHealth((await prototypeApi.health()).data); } catch { /* */ }
      try { setConfig((await prototypeApi.config()).data); } catch { /* */ }
      try { setProjects((await prototypeApi.listProjects()).data || []); } catch { /* */ }
    })();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="owner-diagnostics">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bug className="h-5 w-5 text-re-red" /> Owner diagnostics (technisch)</DialogTitle>
          <DialogDescription>Technische informatie voor de eigenaar. API-sleutels, tokens en wachtwoorden worden nooit getoond.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <section>
            <h4 className="mb-2 font-heading font-semibold text-re-charcoal">Configuratie (aanwezigheid, geen waarden)</h4>
            <div className="flex flex-wrap gap-2">
              <Flag ok={health?.rendyKeyConfigured} label="Rendy API-sleutel" />
              <Flag ok={health?.passwordConfigured} label="Prototype-wachtwoord" />
              <Flag ok={config?.googleMaps?.configured} label="Google Maps" />
              <Flag ok={config?.googleDrive?.configured} label="Google Drive" />
              <Flag ok={config?.dropbox?.configured} label="Dropbox" />
            </div>
            <p className="mt-2 text-xs text-re-muted">De provider is Rendy. API-sleutels, OAuth-tokens en wachtwoorden worden nooit getoond.</p>
          </section>

          <section>
            <h4 className="mb-2 font-heading font-semibold text-re-charcoal">Projecten (provider-details)</h4>
            {projects.length === 0 ? (
              <p className="text-xs text-re-muted">Geen projecten.</p>
            ) : (
              <div className="space-y-2">
                {projects.map((p) => (
                  <div key={p.localProjectId} className="rounded-lg border border-re-line p-3 text-xs" data-testid={`diag-project-${p.localProjectId}`}>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <span className="text-re-muted">Lokaal project-ID</span><span className="font-mono break-all">{p.localProjectId}</span>
                      <span className="text-re-muted">Rendy Listing-ID</span><span className="font-mono break-all">{p.listingId || "—"}</span>
                      <span className="text-re-muted">API-status</span><span>{p.listingStatus || "—"} ({typeof p.progress === "number" ? `${p.progress}%` : "—"})</span>
                      <span className="text-re-muted">Ratio</span><span>{p.ratio || p.draft?.orientation || "—"}</span>
                      <span className="text-re-muted">Aantal foto&apos;s</span><span>{p.draft?.photosMeta?.length ?? p.photoOrder?.length ?? 0}</span>
                      <span className="text-re-muted">Preset keys</span><span className="break-all">{Object.values(p.presetKeys || {}).join(", ") || "—"}</span>
                    </div>
                    {p.ownerNote && <p className="mt-2 rounded bg-re-soft px-2 py-1">Notitie: {p.ownerNote}</p>}
                    {Array.isArray(p.errors) && p.errors.length > 0 && (
                      <pre className="mt-2 max-h-24 overflow-auto rounded bg-re-soft p-2 text-[11px]">{JSON.stringify(p.errors, null, 2)}</pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
